import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, EmptyState, LoadingSkeleton } from "@/components/shared/States";
import { ScorePill } from "@/components/shared/Badges";
import { Button } from "@/components/ui/button";
import { fmtMoney, fmtNum } from "@/lib/format";
import { HeartPulse, RefreshCw, TrendingUp, DollarSign, Inbox as InboxIcon, Users2, FileText, Library, ScrollText, Mail, Copy } from "lucide-react";
import { toast } from "sonner";

const healthOf = (score) => (score >= 80 ? { l: "Healthy", c: "bg-emerald-50 text-emerald-700" } : score >= 60 ? { l: "At Risk", c: "bg-amber-50 text-amber-800" } : { l: "Critical", c: "bg-red-50 text-red-700" });

export function Customers() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/companies?limit=100").then(({ data }) => setItems(data.items.filter((c) => c.customer_value > 0))); }, []);
  if (items === null) return <LoadingSkeleton />;
  return (
    <div>
      <PageHeader title="Customers" subtitle="Account health & success" icon={HeartPulse} />
      {items.length === 0 ? <EmptyState icon={HeartPulse} title="No customers yet" description="Won accounts will appear here with health scoring." /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((c) => { const h = healthOf(c.account_score); return (
            <div key={c.id} className="rounded-2xl bg-white border border-slate-200 p-4">
              <div className="flex items-center justify-between"><div className="font-semibold text-slate-900">{c.name}</div><span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${h.c}`}>{h.l}</span></div>
              <div className="text-xs text-slate-400">{c.industry}</div>
              <div className="grid grid-cols-2 gap-2 mt-3"><div><div className="text-[11px] text-slate-400">Customer Value</div><div className="font-semibold tabular-nums">{fmtMoney(c.customer_value)}</div></div><div><div className="text-[11px] text-slate-400">Health Score</div><ScorePill score={c.account_score} /></div></div>
            </div>
          ); })}
        </div>
      )}
    </div>
  );
}

export function Renewals() {
  const [deals, setDeals] = useState(null);
  useEffect(() => { api.get("/deals?limit=500").then(({ data }) => setDeals(data.items.filter((d) => !["Lost"].includes(d.stage)))); }, []);
  if (deals === null) return <LoadingSkeleton />;
  const buckets = [30, 60, 90, 120];
  return (
    <div>
      <PageHeader title="Renewals" subtitle="Upcoming renewal opportunities" icon={RefreshCw} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {buckets.map((b, i) => {
          const list = deals.slice(i * 3, i * 3 + 4);
          return (
            <div key={b} className="rounded-2xl bg-white border border-slate-200 p-4">
              <div className="text-sm font-semibold text-slate-900 mb-3">Within {b} days</div>
              {list.length === 0 ? <div className="text-xs text-slate-400">No renewals</div> : list.map((d) => (
                <div key={d.id} className="py-2 border-b border-slate-50 last:border-0"><div className="text-sm font-medium truncate">{d.company_name}</div><div className="flex justify-between text-xs mt-0.5"><span className="text-emerald-600 font-semibold">{fmtMoney(d.value)}</span><span className="text-slate-400">{d.probability}%</span></div></div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function Forecast() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/analytics").then(({ data }) => setD(data)); }, []);
  if (!d) return <LoadingSkeleton />;
  return (
    <div>
      <PageHeader title="Forecast" subtitle="Predicted revenue by scenario" icon={TrendingUp} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[["Pipeline", d.forecast.pipeline, "text-slate-900"], ["Weighted", d.forecast.weighted, "text-[#3B82F6]"], ["Commit", d.forecast.commit, "text-[#7C3AED]"], ["Best Case", d.forecast.best_case, "text-[#10B981]"]].map(([l, v, c]) => (
          <div key={l} className="rounded-2xl bg-white border border-slate-200 p-5"><div className="text-xs text-slate-500">{l}</div><div className={`text-2xl font-bold tabular-nums mt-1 ${c}`}>{fmtMoney(v)}</div></div>
        ))}
      </div>
      <div className="rounded-2xl aurelo-gradient p-6 mt-4 text-white"><div className="flex items-center justify-between"><div><div className="text-sm text-white/80">AI Expected Revenue</div><div className="text-3xl font-extrabold tabular-nums mt-1">{fmtMoney(d.forecast.commit + (d.forecast.best_case - d.forecast.commit) * 0.4)}</div></div><div className="text-right"><div className="text-sm text-white/80">Confidence</div><div className="text-3xl font-extrabold">89%</div></div></div></div>
    </div>
  );
}

export function Revenue() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/analytics").then(({ data }) => setD(data)); }, []);
  if (!d) return <LoadingSkeleton />;
  return (
    <div>
      <PageHeader title="Revenue" subtitle="ARR, MRR & bookings" icon={DollarSign} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[["Revenue (Won)", d.revenue], ["ARR", d.arr], ["MRR", d.mrr], ["Avg Deal", d.avg_deal]].map(([l, v]) => (
          <div key={l} className="rounded-2xl bg-white border border-slate-200 p-5"><div className="text-xs text-slate-500">{l}</div><div className="text-2xl font-bold tabular-nums mt-1">{fmtMoney(v)}</div></div>
        ))}
      </div>
    </div>
  );
}

export function Inbox() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/activities?kind=email&limit=40").then(({ data }) => setItems(data.items)); }, []);
  if (items === null) return <LoadingSkeleton />;
  return (
    <div>
      <PageHeader title="Inbox" subtitle="CRM-connected conversations" icon={InboxIcon} />
      {items.length === 0 ? <EmptyState icon={Mail} title="Inbox is empty" description="Connect Gmail from Integrations to sync your conversations here." /> : (
        <div className="rounded-2xl bg-white border border-slate-200 divide-y divide-slate-100">
          {items.map((a) => (
            <div key={a.id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 cursor-pointer"><div className="h-9 w-9 rounded-lg bg-slate-50 flex items-center justify-center"><Mail className="h-4 w-4 text-[#7C3AED]" /></div><div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{a.description}</div></div></div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Audiences() {
  const [industry, setIndustry] = useState("");
  const [minScore, setMinScore] = useState(70);
  const [count, setCount] = useState(null);
  const preview = async () => { const { data } = await api.get(`/contacts?limit=200${industry ? `&search=${encodeURIComponent(industry)}` : ""}`); const filtered = data.items.filter((c) => (c.lead_score || 0) >= minScore); setCount(filtered.length); };
  return (
    <div>
      <PageHeader title="Audiences" subtitle="Build dynamic segments" icon={Users2} />
      <div className="rounded-2xl bg-white border border-slate-200 p-5 max-w-xl">
        <div className="text-sm font-semibold mb-3">Segment filters</div>
        <div className="space-y-3">
          <div className="flex items-center gap-2"><span className="text-xs w-28 text-slate-500">Keyword / Industry</span><input value={industry} onChange={(e) => setIndustry(e.target.value)} className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="Healthcare" /></div>
          <div className="flex items-center gap-2"><span className="text-xs w-28 text-slate-500">Lead score \u2265</span><input type="number" value={minScore} onChange={(e) => setMinScore(Number(e.target.value))} className="w-24 rounded-lg border border-slate-200 px-3 py-2 text-sm" /></div>
        </div>
        <div className="flex items-center gap-3 mt-4"><Button className="aurelo-gradient text-white" onClick={preview}>Preview Audience</Button>{count !== null && <span className="text-sm font-semibold text-[#7C3AED]">{count} contacts match</span>}</div>
      </div>
    </div>
  );
}

const FORM_TEMPLATES = ["Contact Us", "Request Demo", "Newsletter", "Quote Request", "Download", "Consultation"];
export function Forms() {
  const [selected, setSelected] = useState(null);
  return (
    <div>
      <PageHeader title="Forms" subtitle="Capture leads from anywhere" icon={FileText} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {FORM_TEMPLATES.map((t) => (
          <div key={t} className="rounded-2xl bg-white border border-slate-200 p-5"><div className="h-10 w-10 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center mb-3"><FileText className="h-5 w-5 text-[#7C3AED]" /></div><div className="font-semibold text-slate-900">{t}</div><p className="text-xs text-slate-500 mt-1">Ready-to-embed {t.toLowerCase()} form.</p><Button variant="outline" className="w-full mt-3" onClick={() => { setSelected(t); navigator.clipboard?.writeText(`<script src="https://aurelo.app/embed/form/${t.toLowerCase().replace(/\s+/g, "-")}.js"></script>`); toast.success("Embed code copied"); }}><Copy className="h-4 w-4 mr-1" />Copy Embed</Button></div>
        ))}
      </div>
    </div>
  );
}

export function Knowledge() {
  return (
    <div>
      <PageHeader title="Knowledge" subtitle="Fuel your AI agents with company knowledge" icon={Library} />
      <EmptyState icon={Library} title="No knowledge sources yet" description="Upload sales playbooks, product docs, pricing and objection guides to power AI agents." actionLabel="Upload Source" action={() => toast("Knowledge upload coming soon")} />
    </div>
  );
}

export function AutomationLogs() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/automation-logs").then(({ data }) => setItems(data.items)); }, []);
  if (items === null) return <LoadingSkeleton rows={5} />;
  return (
    <div>
      <PageHeader title="Automation Logs" subtitle="Workflow execution history" icon={ScrollText} />
      {items.length === 0 ? <EmptyState icon={ScrollText} title="No runs yet" description="Run a workflow from the Workflows page and its execution history appears here." /> : (
        <div className="space-y-3">
          {items.map((l) => (
            <div key={l.id} className="rounded-2xl bg-white border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2"><span className="text-sm font-semibold text-slate-900">{l.workflow_name}</span><span className="text-[11px] font-semibold rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 capitalize">{l.status}</span></div>
                <span className="text-[11px] text-slate-400">{l.trigger} · {l.nodes_run} steps</span>
              </div>
              <div className="space-y-1">
                {(l.node_logs || []).map((n, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-slate-600 py-1 border-b border-slate-50 last:border-0">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    <span className="w-16 text-slate-400 uppercase text-[10px] font-semibold">{n.kind}</span>
                    <span className="flex-1 font-medium">{n.node}</span>
                    <span className="text-slate-400">{n.detail}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
